import evaluateFieldConditions from '../evaluate-field-conditions';
import { arrayPath } from '../field-paths';
import { hasUnansweredNegationDependency } from './id-requirements';

const isExcludedOnSubmit = (code, excludeOnSubmitCodes) => excludeOnSubmitCodes.includes(code);

/**
 * Submission cleanup only runs for fields we visit in processField. We recurse into
 * field.subFields (and for arrays, each item's subfields). We also recurse into
 * id_requirements_list options[].input_fields and wrapper alt_id_fields (the
 * "can't meet ID requirements" flow) so every field gets the same treatment.
 */

/**
 * Build navigation path with admin parameter preserved
 * @param {string} path - The base navigation path
 * @param {boolean|string} admin - Whether admin parameter should be added (true, 'true', or truthy value)
 * @returns {string} The path with admin parameter appended if admin is truthy
 */
export const buildNavPath = (path, admin) => {
  if (!admin) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}admin=true`;
};

// Checks whether a field path belongs to the current section
export const belongsToCurrentPage = (path, prefixSet) => {
  for (const p of prefixSet) {
    if (path === p || path.startsWith(`${p}.`)) return true;
  }
  return false;
};

/** Build the list of RHF name prefixes that belong to this section */
export const getSectionPrefixes = (sectionKey, sectionFieldPrefixes) => {
  const map = sectionFieldPrefixes?.[sectionKey] || {};
  // map: { [subsection]: string[] } — flatten
  return Object.values(map).flat().filter(Boolean);
};

export const getSubsectionPrefixes = (sectionKey, subsectionCode, sectionFieldPrefixes) => {
  const map = sectionFieldPrefixes?.[sectionKey] || {};
  const arr = map?.[subsectionCode] || [];
  return (arr || []).filter(Boolean);
};

// Collects values from an object by their field path prefixes
export const pickByPrefixes = (obj, prefixSet) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(([k]) => {
      for (const p of prefixSet) {
        if (k === p || k.startsWith(`${p}.`)) return true;
      }
      return false;
    }),
  );

// Walk react-hook-form nested field errors (e.g. address.postal_code) for a leaf message
const getFirstNestedErrorMessage = node => {
  if (!node) return undefined;
  if (typeof node === 'string') return node;
  if (typeof node !== 'object') return undefined;
  if (typeof node.message === 'string' && node.message) {
    return node.message;
  }
  for (const key of Object.keys(node)) {
    if (key === 'ref' || key === 'root' || key === 'types') {
      continue;
    }
    const child = node[key];
    if (child && typeof child === 'object') {
      const found = getFirstNestedErrorMessage(child);
      if (found) return found;
    }
  }
  return undefined;
};

// Helper function to extract error message
export const getErrorMessage = error => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  const nested = getFirstNestedErrorMessage(error);
  if (nested) return nested;
  if (error.type && typeof error.type === 'string') return error.type;
  return undefined;
};

// Merge all values, giving precedence to the latest
// RHF values for the current page (if provided).
export function buildAllFormValues({
  candidateFields,
  watchValues, // latest RHF values for *current* page (optional)
  currentPagePrefixes, // when provided, values from current page comes from watchValues
} = {}) {
  const {
    candidateValues = {},
    hiddenValues = {},
    formValues: reduxFormValues = {},
    idRequirementsMet = {},
  } = candidateFields || {};

  const allValues = { ...candidateValues };

  // Include all sections from Redux EXCEPT current page prefixes
  Object.entries(reduxFormValues || {}).forEach(([key, vals]) => {
    if (vals && typeof vals === 'object') {
      if (currentPagePrefixes && currentPagePrefixes.length > 0) {
        // Filter out current page prefixes from this section
        const filteredVals = { ...vals };
        currentPagePrefixes.forEach(prefix => {
          Object.keys(filteredVals).forEach(fieldKey => {
            if (fieldKey === prefix || fieldKey.startsWith(`${prefix}.`)) {
              delete filteredVals[fieldKey];
            }
          });
        });
        Object.assign(allValues, filteredVals);
      } else {
        // No current page prefixes, include all values
        Object.assign(allValues, vals);
      }
    }
  });

  Object.assign(allValues, hiddenValues || {});
  Object.assign(allValues, idRequirementsMet || {});

  // Finally, overlay current RHF values for the active section
  if (watchValues && typeof watchValues === 'object') {
    Object.assign(allValues, watchValues);
  }

  return allValues;
}

export function collectIdRequirementsFields(subsections) {
  return (subsections || [])
    .flatMap(sub => sub?.fields || [])
    .filter(field => field?.type === 'id_requirements');
}

export function getUnmetIdRequirements(subsections, idRequirementsMet = {}) {
  return collectIdRequirementsFields(subsections).filter(field => {
    if (!field?.code || !field?.hasIdRequirements) return false;
    return !(idRequirementsMet?.[field.code] ?? true);
  });
}

export function hasUncheckedIdRequirementWaiver({
  unmetRequirements,
  candidateFields,
  watchValues,
  currentPagePrefixes,
}) {
  if (!unmetRequirements?.length) return false;

  const allValues = buildAllFormValues({
    candidateFields,
    watchValues,
    currentPagePrefixes,
  });

  return unmetRequirements.some(field => {
    const fulfillmentKey = `${field.code}_not_fulfilled`;
    return !allValues?.[fulfillmentKey];
  });
}

export function evaluateFieldVisibility(
  field,
  allFormValues,
  fieldPathIndex,
  parentField = null,
  arrayIndex = 0,
) {
  if (!field) {
    return false;
  }
  // evaluateFieldConditions treats a missing/unanswered dependency as condition-failed for
  // every operator, including negation ones (is_not/not_in/not_contains) — backwards for
  // those, since "hasn't been answered yet" isn't the same as "answered in a way that hides
  // this field." Without this guard, a field gated on a negated hidden value that's still
  // being recomputed (e.g. right after an upload, before the id_uploads-derived hidden fields
  // it depends on have settled) can flash hidden entirely until that recompute finishes.
  const fieldPasses =
    hasUnansweredNegationDependency(field?.conditions, allFormValues || {}) ||
    evaluateFieldConditions(field?.conditions, allFormValues || {}, {
      parentField,
      arrayIndex,
      fieldPathIndex,
    });
  if (!fieldPasses) {
    return false;
  }

  if (field?.type === 'object' || field?.type === 'id_requirements') {
    const subFields = field?.subFields || [];
    const altIdChildFields = (field?.altIdFields || field?.alt_id_fields || []).flatMap(
      group => group?.subFields || group?.sub_fields || [],
    );
    const allChildFields = [...subFields, ...altIdChildFields];
    if (allChildFields.length === 0) {
      return false;
    }
    return allChildFields.some(child =>
      evaluateFieldConditions(child?.conditions, allFormValues || {}, {
        parentCode: field?.code,
        fieldPathIndex,
      }),
    );
  }
  return true;
}

export function evaluateNestedFieldConditions(
  subField,
  parentField,
  allFormValues,
  fieldPathIndex,
  arrayIndex = 0,
) {
  if (!subField?.conditions) {
    return true;
  }
  return evaluateFieldConditions(subField.conditions, allFormValues || {}, {
    parentCode: parentField?.code,
    arrayIndex,
    fieldPathIndex,
    arraySubFieldCodes: (parentField?.subFields ?? parentField?.sub_fields ?? [])
      .map(field => field.code)
      .filter(Boolean),
  });
}

export const prepareValuesForSubmission = (
  watchValues,
  candidateValues,
  hiddenValues,
  reduxFormValues = {},
  sections,
  sectionKey,
  fieldPathIndex,
  excludeOnSubmitCodes,
  currentSubsectionPrefixes = null,
  sectionFieldPrefixes = {},
  existingValues = {},
) => {
  if (!sectionKey) return {};

  // Deep clone watchValues to avoid mutating frozen/proxied objects from React Hook Form
  let cleanedWatchValues = {};
  Object.keys(watchValues || {}).forEach(key => {
    const value = watchValues[key];
    if (Array.isArray(value)) {
      // Deep clone array items
      cleanedWatchValues[key] = value.map(item =>
        item && typeof item === 'object' ? { ...item } : item,
      );
    } else {
      cleanedWatchValues[key] = value;
    }
  });

  const allValues = { ...candidateValues, ...watchValues, ...hiddenValues };

  // Preserve values from completed checks
  // Get all base field codes from sectionFieldPrefixes (flattened across all sections)
  const currentSectionsBaseCodes = new Set();
  Object.values(sectionFieldPrefixes || {}).forEach(sectionMap => {
    Object.values(sectionMap || {}).forEach(prefixes => {
      prefixes.forEach(prefix => {
        if (prefix) {
          currentSectionsBaseCodes.add(prefix);
        }
      });
    });
  });

  // Preserve values from completed checks/extracted fields that aren't in any section
  const preservedValues = {};
  Object.keys(existingValues || {}).forEach(key => {
    const baseCode = key.split('.')[0];
    if (!currentSectionsBaseCodes.has(baseCode)) {
      preservedValues[key] = existingValues[key];
    }
  });

  const removeSubFieldValues = field => {
    (field.subFields || []).forEach(subField => {
      delete cleanedWatchValues[subField.code];
      removeSubFieldValues(subField);
    });
  };

  // Define processField function first
  const processField = (field, allValues, parentCode = null, arrayIndex = 0, fieldPathIndex) => {
    const isArraySubfield = parentCode && arrayIndex !== undefined;
    const code = isArraySubfield ? arrayPath(parentCode, arrayIndex, field?.code) : field?.code;

    // Check if field should be visible
    let conditionsMet = true;
    if (field.conditions) {
      conditionsMet = evaluateFieldConditions(field.conditions, allValues, {
        parentCode,
        arrayIndex,
        fieldPathIndex,
      });
    }

    // If conditions not met, delete from cleanedWatchValues
    if (!conditionsMet) {
      if (isArraySubfield) {
        // Delete from array item
        const arrayField = cleanedWatchValues[parentCode];
        if (
          Array.isArray(arrayField) &&
          arrayField[arrayIndex] &&
          typeof arrayField[arrayIndex] === 'object'
        ) {
          delete arrayField[arrayIndex][field.code];
        }
      } else {
        delete cleanedWatchValues[code];
        removeSubFieldValues(field);
      }
      return;
    }

    // Check if field should be excluded on submit
    if (isExcludedOnSubmit(field.code, excludeOnSubmitCodes)) {
      if (isArraySubfield) {
        // Delete from array item
        const arrayField = cleanedWatchValues[parentCode];
        if (
          Array.isArray(arrayField) &&
          arrayField[arrayIndex] &&
          typeof arrayField[arrayIndex] === 'object'
        ) {
          delete arrayField[arrayIndex][field.code];
        }
      } else {
        delete cleanedWatchValues[code];
      }
      return;
    }

    // Process subfields FIRST (before filtering out null/undefined values)
    if (field.subFields) {
      // Handle array types that need special processing
      if (field.type === 'array' || field.type === 'dynamic_array') {
        const arrayLength =
          field.type === 'dynamic_array'
            ? cleanedWatchValues[field.code]?.length || 1
            : field.size || 1;

        for (let i = 0; i < arrayLength; i++) {
          field.subFields.forEach(subField => {
            processField(subField, allValues, field.code, i, fieldPathIndex);
          });
        }
      } else {
        // Handle all other field types with subfields
        field.subFields.forEach(subField => {
          processField(subField, allValues, null, 0, fieldPathIndex);
        });
      }
    }

    // id_requirements_list: option input_fields are conceptually subfields but live in options[].input_fields.
    // Recurse into them with processField so they get the same treatment as subfields (exclude, null filter, etc.).
    if (field.type === 'id_requirements_list' && Array.isArray(field.options)) {
      field.options.forEach(option => {
        (option.input_fields || []).forEach(innerField => {
          processField(innerField, allValues, null, 0, fieldPathIndex);
        });
      });
    }

    // alt_id_fields (e.g. "can't meet ID requirements" flow): live under wrapper.alt_id_fields / altIdFields,
    // each group has subFields. Treat like subfields so they get the same cleaning and exclude-on-submit behaviour.
    const altIdGroups = field.alt_id_fields || field.altIdFields;
    if (Array.isArray(altIdGroups) && altIdGroups.length > 0) {
      altIdGroups.forEach(altGroup => {
        const innerFields = altGroup.subFields || altGroup.sub_fields || [];
        innerFields.forEach(innerField => {
          processField(innerField, allValues, null, 0, fieldPathIndex);
        });
      });
    }

    // Filter out null and undefined values as we process each field
    let fieldValue;
    if (isArraySubfield) {
      const arrayField = cleanedWatchValues[parentCode];
      if (
        Array.isArray(arrayField) &&
        arrayField[arrayIndex] &&
        typeof arrayField[arrayIndex] === 'object'
      ) {
        fieldValue = arrayField[arrayIndex][field.code];
        if (fieldValue === null || fieldValue === undefined) {
          delete arrayField[arrayIndex][field.code];
          return;
        }
      } else {
        return; // Array item doesn't exist, skip
      }
    } else {
      fieldValue = cleanedWatchValues[code];
      if (fieldValue === null || fieldValue === undefined) {
        delete cleanedWatchValues[code];
        return;
      }
    }

    // Handle address fields - clean empty values and remove if completely empty
    if (field.type === 'address') {
      if (fieldValue && typeof fieldValue === 'object') {
        // Remove empty key-value pairs from the address object
        const cleanedAddress = {};
        Object.entries(fieldValue).forEach(([key, value]) => {
          if (value !== '' && value != null && value !== undefined) {
            cleanedAddress[key] = value;
          }
        });

        // If the address object is now empty, remove the entire field
        if (Object.keys(cleanedAddress).length === 0) {
          if (isArraySubfield) {
            const arrayField = cleanedWatchValues[parentCode];
            if (
              Array.isArray(arrayField) &&
              arrayField[arrayIndex] &&
              typeof arrayField[arrayIndex] === 'object'
            ) {
              delete arrayField[arrayIndex][field.code];
            }
          } else {
            delete cleanedWatchValues[code];
          }
          return;
        }

        // Update the field value with the cleaned address
        if (isArraySubfield) {
          const arrayField = cleanedWatchValues[parentCode];
          if (
            Array.isArray(arrayField) &&
            arrayField[arrayIndex] &&
            typeof arrayField[arrayIndex] === 'object'
          ) {
            arrayField[arrayIndex][field.code] = cleanedAddress;
          }
        } else {
          cleanedWatchValues[code] = cleanedAddress;
        }
      }
    }
  };

  // Find the current section and get its subsections, then flatten one level
  const currentSection = sections.find(section => section.code === sectionKey);
  const currentSectionFields =
    currentSection?.subsections?.flatMap(subsection => subsection.fields || []) || [];

  // Process all fields in the current section (like the old version)
  // The processField function will handle filtering based on conditions and excludeOnSubmit
  currentSectionFields.forEach(field => {
    processField(field, allValues, null, 0, fieldPathIndex);
  });

  // Filter out current page's values from candidateValues
  let filteredCandidateValues = { ...candidateValues };
  (currentSubsectionPrefixes || []).forEach(prefix => {
    Object.keys(filteredCandidateValues).forEach(key => {
      if (key === prefix || key.startsWith(`${prefix}.`)) {
        delete filteredCandidateValues[key];
      }
    });
  });

  // Filter hiddenValues to remove excludeOnSubmit fields and null/undefined values
  const filteredHiddenValues = { ...hiddenValues };
  Object.keys(filteredHiddenValues).forEach(key => {
    if (isExcludedOnSubmit(key, excludeOnSubmitCodes)) {
      delete filteredHiddenValues[key];
    } else if (filteredHiddenValues[key] === null || filteredHiddenValues[key] === undefined) {
      delete filteredHiddenValues[key];
    }
  });

  // Filter out flattened field names (like employment_history.0.field_name) from cleanedWatchValues
  const filteredCleanedWatchValues = {};
  Object.keys(cleanedWatchValues).forEach(key => {
    // Only include keys that don't contain dots (not flattened array field names)
    if (!key.includes('.')) {
      filteredCleanedWatchValues[key] = cleanedWatchValues[key];
    }
  });

  // Filter out current page's values from existing section values
  // Match exact prefix or prefix followed by a dot (to avoid partial matches)
  let filteredSectionValues = { ...(reduxFormValues[sectionKey] || {}) };
  (currentSubsectionPrefixes || []).forEach(prefix => {
    Object.keys(filteredSectionValues).forEach(key => {
      if (key === prefix || key.startsWith(`${prefix}.`)) {
        delete filteredSectionValues[key];
      }
    });
  });

  // Combine filtered existing values with filtered watch values
  const updatedSectionValues = {
    ...filteredSectionValues,
    ...filteredCleanedWatchValues,
  };

  // Include section values in updatedCandidateValues so they get saved to the database
  const updatedCandidateValues = {
    ...preservedValues,
    ...filteredCandidateValues,
    ...filteredSectionValues, // Include values from formValues for this section
    ...filteredCleanedWatchValues,
    ...filteredHiddenValues,
  };

  return { updatedCandidateValues, updatedSectionValues };
};
